// Real manufacturer GBB packages (ported from Audit Genius retailPackages.ts)
export type ManufacturerKey = "gaf" | "certainteed" | "owens_corning";
export type PackageTier = "good" | "better" | "best";

export interface PackageLineItem {
  item: string;
  description: string;
  unit: string;
  category: "Material" | "Labor";
  midPrice: number;
  qtyFormula: string;
  conditional?: boolean;
  conditionKey?: string;
}

export interface ManufacturerPackage {
  manufacturer: ManufacturerKey;
  manufacturerLabel: string;
  tier: PackageTier;
  tierLabel: string;
  systemName: string;
  warrantyName: string;
  highlights: string[];
  materials: PackageLineItem[];
  labor: PackageLineItem[];
}

const SHARED_LABOR: PackageLineItem[] = [
  { item: "Roof Tear-Off", description: "Remove existing shingles per layer", unit: "SQ", category: "Labor", midPrice: 65, qtyFormula: "sq" },
  { item: "Install Roofing System", description: "Install complete roofing system", unit: "SQ", category: "Labor", midPrice: 225, qtyFormula: "sq" },
  { item: "Steep Slope Charge", description: "Pitch 7/12–9/12", unit: "SQ", category: "Labor", midPrice: 22.5, qtyFormula: "sq", conditional: true, conditionKey: "steep" },
  { item: "Detach & Reset Gutters", description: "Remove and reinstall gutters", unit: "LF", category: "Labor", midPrice: 10, qtyFormula: "eave_lf" },
  { item: "Dumpster & Disposal", description: "Dumpster delivery and dump fees", unit: "EA", category: "Labor", midPrice: 550, qtyFormula: "dumpster" },
];

const SHARED_ACCESSORIES: PackageLineItem[] = [
  { item: "Eave Metal", description: "Aluminum edge metal at eaves", unit: "LF", category: "Material", midPrice: 2.25, qtyFormula: "eave_lf" },
  { item: "Drip Edge", description: "Aluminum edge metal at rakes", unit: "LF", category: "Material", midPrice: 2.25, qtyFormula: "rake_lf" },
  { item: "Valley Metal", description: "W valley flashing", unit: "LF", category: "Material", midPrice: 4.50, qtyFormula: "valley_lf" },
  { item: "Step Flashing", description: "Individual wall flashing", unit: "PC", category: "Material", midPrice: 1, qtyFormula: "step_flash" },
  { item: "Pipe Boots", description: "Rubber pipe flashing", unit: "EA", category: "Material", midPrice: 28, qtyFormula: "pipe_count" },
  { item: "Coil Nails 1-1/4", description: "Roofing coil nails", unit: "BOX", category: "Material", midPrice: 45, qtyFormula: "nail_boxes" },
  { item: "Cap Nails", description: "Underlayment fasteners", unit: "BOX", category: "Material", midPrice: 37.5, qtyFormula: "cap_nail_boxes" },
];

const mk = (
  manufacturer: ManufacturerKey,
  manufacturerLabel: string,
  tier: PackageTier,
  systemName: string,
  warrantyName: string,
  highlights: string[],
  shingle: { name: string; price: number },
  ridge: { name: string; price: number; lfPerBdl: number },
  underlayment: { name: string; price: number },
  iw: { name: string; price: number },
  ridgeVent: { name: string; price: number },
  warrantyFee: number,
): ManufacturerPackage => ({
  manufacturer, manufacturerLabel, tier,
  tierLabel: tier.charAt(0).toUpperCase() + tier.slice(1),
  systemName, warrantyName, highlights,
  materials: [
    { item: shingle.name, description: "Architectural field shingles", unit: "SQ", category: "Material", midPrice: shingle.price, qtyFormula: "sq" },
    { item: `${manufacturerLabel} Starter Strip`, description: "Starter shingles at eaves and rakes", unit: "BDL", category: "Material", midPrice: 45, qtyFormula: "starter_bdl" },
    { item: ridge.name, description: "Ridge cap shingles", unit: "BDL", category: "Material", midPrice: ridge.price, qtyFormula: `ridge_bdl_${ridge.lfPerBdl}` },
    { item: underlayment.name, description: "Synthetic underlayment", unit: "RL", category: "Material", midPrice: underlayment.price, qtyFormula: "underlayment_rl" },
    { item: iw.name, description: "Ice & water shield membrane", unit: "RL", category: "Material", midPrice: iw.price, qtyFormula: "iw_rl" },
    { item: ridgeVent.name, description: "Ridge ventilation", unit: "LF", category: "Material", midPrice: ridgeVent.price, qtyFormula: "ridge_lf" },
    ...SHARED_ACCESSORIES,
  ],
  labor: [
    ...SHARED_LABOR,
    { item: warrantyName, description: "Warranty registration", unit: "EA", category: "Labor", midPrice: warrantyFee, qtyFormula: "fixed_1" },
  ],
});

export const ALL_PACKAGES: ManufacturerPackage[] = [
  mk("gaf", "GAF", "good", "GAF Timberline HDZ Roof System", "GAF System Plus Warranty",
    ["Timberline HDZ Shingles", "FeltBuster Underlayment", "Seal-A-Ridge Caps", "System Plus Warranty"],
    { name: "GAF Timberline HDZ", price: 135 }, { name: "GAF Seal-A-Ridge", price: 57.5, lfPerBdl: 25 },
    { name: "GAF FeltBuster", price: 100 }, { name: "GAF WeatherWatch", price: 120 },
    { name: "Continuous Ridge Vent", price: 5.5 }, 150),
  mk("gaf", "GAF", "better", "GAF Timberline HDZ Roof System", "GAF Silver Pledge Warranty",
    ["Timberline HDZ Shingles", "Deck-Armor Premium Underlayment", "TimberTex Ridge Caps", "Silver Pledge Warranty"],
    { name: "GAF Timberline HDZ", price: 135 }, { name: "GAF TimberTex", price: 77.5, lfPerBdl: 20 },
    { name: "GAF Deck-Armor", price: 140 }, { name: "GAF WeatherWatch", price: 120 },
    { name: "GAF Cobra Ridge Vent", price: 9 }, 350),
  mk("gaf", "GAF", "best", "GAF Timberline Ultra HDZ Roof System", "GAF Golden Pledge Warranty",
    ["Timberline Ultra HDZ Shingles", "Deck-Armor + StormGuard", "TimberTex Ridge Caps", "Golden Pledge Warranty"],
    { name: "GAF Timberline Ultra HDZ", price: 170 }, { name: "GAF TimberTex", price: 77.5, lfPerBdl: 20 },
    { name: "GAF Deck-Armor", price: 140 }, { name: "GAF StormGuard", price: 150 },
    { name: "GAF Cobra Rigid Vent 3", price: 13 }, 1250),

  mk("certainteed", "CertainTeed", "good", "CertainTeed Landmark Roof System", "CertainTeed 3-Star SureStart Warranty",
    ["Landmark Shingles", "RoofRunner Underlayment", "Shadow Ridge Caps", "3-Star Warranty"],
    { name: "CertainTeed Landmark", price: 135 }, { name: "CertainTeed Shadow Ridge", price: 57.5, lfPerBdl: 25 },
    { name: "CertainTeed RoofRunner", price: 90 }, { name: "CertainTeed WinterGuard", price: 120 },
    { name: "Continuous Ridge Vent", price: 5.5 }, 75),
  mk("certainteed", "CertainTeed", "better", "CertainTeed Landmark Pro Roof System", "CertainTeed 4-Star SureStart Warranty",
    ["Landmark Pro Shingles", "DiamondDeck Underlayment", "CedarCrest Ridge Caps", "4-Star Warranty"],
    { name: "CertainTeed Landmark Pro", price: 155 }, { name: "CertainTeed CedarCrest", price: 77.5, lfPerBdl: 20 },
    { name: "CertainTeed DiamondDeck", price: 130 }, { name: "CertainTeed WinterGuard", price: 120 },
    { name: "CertainTeed Ridge Vent", price: 9 }, 175),
  mk("certainteed", "CertainTeed", "best", "CertainTeed Presidential Shake Roof System", "CertainTeed 5-Star SureStart Warranty",
    ["Presidential Shake Shingles", "DiamondDeck + WinterGuard HT", "CedarCrest Ridge Caps", "5-Star Warranty"],
    { name: "CertainTeed Presidential Shake", price: 275 }, { name: "CertainTeed CedarCrest", price: 77.5, lfPerBdl: 20 },
    { name: "CertainTeed DiamondDeck", price: 130 }, { name: "CertainTeed WinterGuard HT", price: 150 },
    { name: "CertainTeed Ridge Vent", price: 11 }, 550),

  mk("owens_corning", "Owens Corning", "good", "Owens Corning Duration Roof System", "OC System Warranty",
    ["Duration Shingles", "OC Synthetic Underlayment", "Hip & Ridge Caps", "System Warranty"],
    { name: "OC Duration", price: 135 }, { name: "OC Hip & Ridge", price: 57.5, lfPerBdl: 25 },
    { name: "OC Synthetic Underlayment", price: 90 }, { name: "OC Ice & Water Shield", price: 120 },
    { name: "Continuous Ridge Vent", price: 5.5 }, 150),
  mk("owens_corning", "Owens Corning", "better", "OC Duration Designer Roof System", "OC Preferred Warranty",
    ["Duration Designer Shingles", "Premium Underlayment", "ProEdge Ridge Caps", "Preferred Warranty"],
    { name: "OC Duration Designer", price: 170 }, { name: "OC ProEdge Hip & Ridge", price: 77.5, lfPerBdl: 20 },
    { name: "OC Premium Synthetic", price: 130 }, { name: "OC Ice & Water Shield", price: 120 },
    { name: "OC Ridge Vent", price: 9 }, 175),
  mk("owens_corning", "Owens Corning", "best", "OC Duration Premium Roof System", "OC Platinum Warranty",
    ["Duration Premium Shingles", "Premium Underlayment + High Temp I&W", "ProEdge Ridge Caps", "Platinum Warranty"],
    { name: "OC Duration Premium", price: 210 }, { name: "OC ProEdge Hip & Ridge", price: 77.5, lfPerBdl: 20 },
    { name: "OC Premium Synthetic", price: 130 }, { name: "OC High Temp Ice Shield", price: 150 },
    { name: "OC Rigid Ridge Vent", price: 13 }, 550),
];

export const MANUFACTURERS: { key: ManufacturerKey; label: string; description: string }[] = [
  { key: "gaf", label: "GAF", description: "America's #1 shingle brand" },
  { key: "certainteed", label: "CertainTeed", description: "Premium quality since 1904" },
  { key: "owens_corning", label: "Owens Corning", description: "Total Protection Roofing System" },
];

export function getPackages(m: ManufacturerKey) {
  const pkgs = ALL_PACKAGES.filter((p) => p.manufacturer === m);
  return {
    good: pkgs.find((p) => p.tier === "good")!,
    better: pkgs.find((p) => p.tier === "better")!,
    best: pkgs.find((p) => p.tier === "best")!,
  };
}
